import { apiClient, type Paginated } from '@/core/api';
import { triggerBlobDownload } from '@/features/finance/staff-invoices/api';
import type { StaffInvoice } from '@/features/finance/staff-invoices';
import type { EmployeeBankAccount } from '@/features/hrm/employees/bankAccounts';
import type { GoalStatus, PDP, Skill } from '@/features/hrm/development/api';
import type { Onboarding, OnboardingItem } from '@/features/hrm/onboarding';
import type { PerformanceReview } from '@/features/hrm/reviews';
import type { TimeOff } from '@/features/hrm/time-off';
import type {
  CabinetDocument,
  CabinetEmployeeSkill,
  CabinetPayslip,
  CabinetProfile,
  CabinetSalaryRecord,
  CabinetSkillInput,
  CabinetTimeOffInput,
} from './types';

const PROFILE = '/cabinet/profile/';
export const SALARY_BASE = '/cabinet/salary/';
export const PAYSLIPS_BASE = '/cabinet/payslips/';
export const REVIEWS_BASE = '/cabinet/reviews/';
export const PDP_BASE = '/cabinet/pdp/';
export const TIMEOFF_BASE = '/cabinet/time-off/';
export const ONBOARDING_BASE = '/cabinet/onboarding/';
const ONBOARDING_ITEMS = '/cabinet/onboarding-items/';
export const SKILLS_BASE = '/cabinet/skills/';
const SKILL_CATALOG = '/cabinet/skill-catalog/';
export const INVOICES_BASE = '/cabinet/invoices/';
export const DOCUMENTS_BASE = '/cabinet/documents/';
export const BANK_ACCOUNTS_BASE = '/cabinet/bank-accounts/';

/** Common list params for the CRM-style cabinet lists. */
export interface CabinetListParams {
  page?: number;
  search?: string;
  ordering?: string;
}

export async function getCabinetProfile(): Promise<CabinetProfile> {
  const { data } = await apiClient.get<CabinetProfile>(PROFILE);
  return data;
}

export async function getCabinetSalary(
  params: CabinetListParams = {},
): Promise<Paginated<CabinetSalaryRecord>> {
  const { data } = await apiClient.get<Paginated<CabinetSalaryRecord>>(SALARY_BASE, { params });
  return data;
}

export async function getCabinetPayslips(
  params: CabinetListParams = {},
): Promise<Paginated<CabinetPayslip>> {
  const { data } = await apiClient.get<Paginated<CabinetPayslip>>(PAYSLIPS_BASE, { params });
  return data;
}

export interface CabinetReviewListParams {
  page?: number;
  search?: string;
  /** DRF OrderingFilter value, e.g. `-review_date`. */
  ordering?: string;
}

export async function getCabinetReviews(
  params: CabinetReviewListParams = {},
): Promise<Paginated<PerformanceReview>> {
  const { data } = await apiClient.get<Paginated<PerformanceReview>>(REVIEWS_BASE, { params });
  return data;
}

export async function getCabinetReview(publicId: string): Promise<PerformanceReview> {
  const { data } = await apiClient.get<PerformanceReview>(`${REVIEWS_BASE}${publicId}/`);
  return data;
}

/** Employee confirms they've read & discussed the review (submitted → acknowledged). */
export async function acknowledgeCabinetReview(publicId: string): Promise<PerformanceReview> {
  const { data } = await apiClient.post<PerformanceReview>(`${REVIEWS_BASE}${publicId}/acknowledge/`);
  return data;
}

/** Employee ticks one of their own review's action items done/undone. */
export async function setCabinetReviewActionItemDone(
  id: number,
  isDone: boolean,
): Promise<void> {
  await apiClient.patch(`/cabinet/review-items/${id}/`, { is_done: isDone });
}

export async function getCabinetPdps(params: CabinetListParams = {}): Promise<Paginated<PDP>> {
  const { data } = await apiClient.get<Paginated<PDP>>(PDP_BASE, { params });
  return data;
}

export async function getCabinetPdp(publicId: string): Promise<PDP> {
  const { data } = await apiClient.get<PDP>(`${PDP_BASE}${publicId}/`);
  return data;
}

/** Employee moves one of their own development goals open → in progress → done. */
export async function setCabinetPdpGoalStatus(id: number, status: GoalStatus): Promise<void> {
  await apiClient.patch(`/cabinet/pdp-goals/${id}/`, { status });
}

// ---- Time Off ----
export interface CabinetTimeOffListParams {
  page?: number;
  search?: string;
  /** DRF OrderingFilter value, e.g. `-start_date`. */
  ordering?: string;
}

export async function listCabinetTimeOff(
  params: CabinetTimeOffListParams = {},
): Promise<Paginated<TimeOff>> {
  const { data } = await apiClient.get<Paginated<TimeOff>>(TIMEOFF_BASE, { params });
  return data;
}

export async function createCabinetTimeOff(input: CabinetTimeOffInput): Promise<TimeOff> {
  const { data } = await apiClient.post<TimeOff>(TIMEOFF_BASE, input);
  return data;
}

export async function updateCabinetTimeOff(
  publicId: string,
  input: Partial<CabinetTimeOffInput>,
): Promise<TimeOff> {
  const { data } = await apiClient.patch<TimeOff>(`${TIMEOFF_BASE}${publicId}/`, input);
  return data;
}

export async function cancelCabinetTimeOff(publicId: string): Promise<TimeOff> {
  const { data } = await apiClient.post<TimeOff>(`${TIMEOFF_BASE}${publicId}/cancel/`);
  return data;
}

// ---- Onboarding ----
export async function getCabinetOnboarding(
  params: CabinetListParams = {},
): Promise<Paginated<Onboarding>> {
  const { data } = await apiClient.get<Paginated<Onboarding>>(ONBOARDING_BASE, { params });
  return data;
}

export async function getCabinetOnboardingPlan(publicId: string): Promise<Onboarding> {
  const { data } = await apiClient.get<Onboarding>(`${ONBOARDING_BASE}${publicId}/`);
  return data;
}

/** Employee begins a checklist (to do → in progress), stamping started_on. */
export async function startCabinetOnboarding(publicId: string): Promise<Onboarding> {
  const { data } = await apiClient.post<Onboarding>(`${ONBOARDING_BASE}${publicId}/start/`);
  return data;
}

export async function markCabinetOnboardingItem(
  id: number,
  isDone: boolean,
): Promise<OnboardingItem> {
  const { data } = await apiClient.patch<OnboardingItem>(`${ONBOARDING_ITEMS}${id}/`, {
    is_done: isDone,
  });
  return data;
}

// ---- Skills ----
export async function listCabinetSkills(
  params: CabinetListParams = {},
): Promise<Paginated<CabinetEmployeeSkill>> {
  const { data } = await apiClient.get<Paginated<CabinetEmployeeSkill>>(SKILLS_BASE, { params });
  return data;
}

export async function getCabinetSkillCatalog(search?: string): Promise<Skill[]> {
  const { data } = await apiClient.get<Paginated<Skill>>(SKILL_CATALOG, {
    params: search ? { search } : undefined,
  });
  return data.results;
}

/** Add a skill to the org catalog by name (get-or-create) — free-text entry. */
export async function createCabinetSkillCatalogEntry(name: string): Promise<Skill> {
  const { data } = await apiClient.post<Skill>(SKILL_CATALOG, { name });
  return data;
}

export async function createCabinetSkill(input: CabinetSkillInput): Promise<CabinetEmployeeSkill> {
  const { data } = await apiClient.post<CabinetEmployeeSkill>(SKILLS_BASE, input);
  return data;
}

export async function updateCabinetSkill(
  id: number,
  input: Partial<CabinetSkillInput>,
): Promise<CabinetEmployeeSkill> {
  const { data } = await apiClient.patch<CabinetEmployeeSkill>(`${SKILLS_BASE}${id}/`, input);
  return data;
}

export async function deleteCabinetSkill(id: number): Promise<void> {
  await apiClient.delete(`${SKILLS_BASE}${id}/`);
}

/** Set/clear a catalog skill's category inline (org-shared catalog entry). */
export async function setCabinetSkillCategory(skillId: number, category: string): Promise<Skill> {
  const { data } = await apiClient.patch<Skill>(`${SKILL_CATALOG}${skillId}/`, { category });
  return data;
}

// ---- Bank accounts (own payout accounts, read-only) ----
// The employee can only VIEW their payout accounts; Finance/HR maintain them
// (editing mid-cycle could break the pay flow). The cabinet endpoint is
// read-only server-side too.
export async function listCabinetBankAccounts(
  params: CabinetListParams = {},
): Promise<Paginated<EmployeeBankAccount>> {
  const { data } = await apiClient.get<Paginated<EmployeeBankAccount>>(BANK_ACCOUNTS_BASE, {
    params,
  });
  return data;
}

// ---- Invoices (own staff invoices) ----
export async function listCabinetInvoices(
  params: CabinetListParams = {},
): Promise<Paginated<StaffInvoice>> {
  const { data } = await apiClient.get<Paginated<StaffInvoice>>(INVOICES_BASE, { params });
  return data;
}

export async function getCabinetInvoice(publicId: string): Promise<StaffInvoice> {
  const { data } = await apiClient.get<StaffInvoice>(`${INVOICES_BASE}${publicId}/`);
  return data;
}

export async function signCabinetInvoice(publicId: string): Promise<StaffInvoice> {
  const { data } = await apiClient.post<StaffInvoice>(`${INVOICES_BASE}${publicId}/sign/`);
  return data;
}

export async function getCabinetInvoiceBlob(publicId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${INVOICES_BASE}${publicId}/download/`, {
    responseType: 'blob',
  });
  return data;
}

export async function downloadCabinetInvoice(publicId: string, filename: string): Promise<void> {
  triggerBlobDownload(await getCabinetInvoiceBlob(publicId), filename);
}

// ---- Documents (own linked documents: NDA, SOW, agreements…) ----
export async function listCabinetDocuments(
  params: CabinetListParams = {},
): Promise<Paginated<CabinetDocument>> {
  const { data } = await apiClient.get<Paginated<CabinetDocument>>(DOCUMENTS_BASE, { params });
  return data;
}

export async function signCabinetDocument(id: number): Promise<CabinetDocument> {
  const { data } = await apiClient.post<CabinetDocument>(`${DOCUMENTS_BASE}${id}/sign/`);
  return data;
}

export async function getCabinetDocumentBlob(id: number): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${DOCUMENTS_BASE}${id}/download/`, {
    responseType: 'blob',
  });
  return data;
}

export async function downloadCabinetDocument(id: number, filename: string): Promise<void> {
  triggerBlobDownload(await getCabinetDocumentBlob(id), filename);
}
